/** Custom hook to provide FNC functionality */
import { useEffect, useRef } from 'react';
import { debounce } from "lodash";

import {
    AGENT_NON_VOICE_STATES,
    CHANNEL_STATE_MAP,
    READY_MENU_ID,
    NOT_READY_MENU_ID,
    CHAT_LABEL,
    EMAIL_LABEL,
    CHAT_ICON_NAME,
    EMAIL_ICON_NAME,
    SUPPORTED_MEDIA_CHANNELS,
    AVAILABLE_MEDIA_CLASS
} from './constants';
import { i18n } from '../../utils/GadgetUtils';
import { ACTIONS, ImiDesktopNotificationManager } from './ImiDesktopNotificationManager';
import { FINESSE_NON_VOICE_RESTORE_STATE_KEY, FINESSE_NON_VOICE_SIGNED_IN_KEY, saveToSessionStorage, getFromSessionStorage, removeFromSessionStorage } from '../../utils/cacheUtils';

const I18n = window.top.finesse.utilities.I18n;
const CACHED_FINESSE_NON_VOICE_SIGNED_IN = !!getFromSessionStorage(FINESSE_NON_VOICE_SIGNED_IN_KEY);
console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [] : CACHED_FINESSE_NON_VOICE_SIGNED_IN : ', CACHED_FINESSE_NON_VOICE_SIGNED_IN);

const useDigitalChannel = (INITIAL_PROPS) => {

    const mediaDomainList = useRef([]);
    const allMediaObjects = useRef([]);
    const mediaList = useRef([]);
    const chatChannelData = useRef({});
    const emailChannelData = useRef({});
    const channelsSignInStatus = useRef({});
    const allChannelIds = useRef({ chat: [], email: [], social: [] });
    const notReadyReasonCodesRef = useRef([]);
    const fncNonRoutableMediaIds = useRef({});
    const debouncedFncChatUpdate = debounce((channelType) => {
        updateFncChannelData(channelType);
    }, 150);
    const debouncedFncEmailUpdate = debounce((channelType) => {
        updateFncChannelData(channelType);
    }, 150);
    const debouncedCacheMediaStatesDuringRestoring = debounce(() => {
        console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] : debouncedCacheMediaStatesDuringRestoring : caching all media states');
        cacheMediaStates();
    }, 7000);
    const recoveredRef = useRef(false);
    const chatChannelId = useRef('');
    const emailChannelId = useRef('');
    const nonRoutableHandled = useRef(false);
    const mediaIdsOfRonaDuringReconnecting = useRef([]);
    const registeredChannelsRef = useRef({chat: false, email: false});
    const restoreAfterFailoverInProgress = useRef(false);

    useEffect(() => {

        return () => {
            removeChannel(chatChannelData.current.channelId);
            removeChannel(emailChannelData.current.channelId);
        };
    }, []);

    useEffect(() => {
        console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [useEffect INITIAL_PROPS.recovered] : ', INITIAL_PROPS.recovered);
        recoveredRef.current = INITIAL_PROPS.recovered;

    }, [INITIAL_PROPS.recovered]);

    const ACTION_TIMEOUT_SECONDS = 30,

    CHANNEL_TYPES = {
        CHAT: 'chat',
        EMAIL: 'email',
        SOCIAL: 'social'
    };

    let containerService = window.finesse.containerservices.ContainerServices.init(),

        channelService = window.finesse.digital.ChannelService.init(containerService),

        logger;
    
        function getMediaType(id) {
            let mediaDomain = mediaDomainList.current.find((media) => media.id === id);
            return mediaDomain?.channel;
        }

        function getMediaClass(id) {
            let mediaDomain = mediaDomainList.current.find((media) => media.id === id);
            return mediaDomain?.mediaClass;
        }

        function getChannelType(id) {
            let mediaClass = getMediaClass(id);
            if (!mediaClass) {
                return;
            }

            if (AVAILABLE_MEDIA_CLASS.EMAIL.toLowerCase() === mediaClass.toLowerCase()) {
                return CHANNEL_TYPES.EMAIL;
            } else if (AVAILABLE_MEDIA_CLASS.CHAT.toLowerCase() === mediaClass.toLowerCase()) {
                return CHANNEL_TYPES.CHAT;
            } else if (AVAILABLE_MEDIA_CLASS.SOCIAL.toLowerCase() === mediaClass.toLowerCase()) {
                return CHANNEL_TYPES.SOCIAL;
            }
        }

        /**
         * Return the ReasonCode JSON object based on ReasonCode Id
         * @param {*} id
         */
         function findNotReadyReasonCodeById (id) {
            return notReadyReasonCodesRef.current.find((reasonCode) => reasonCode.id === id);
        }

        function getCustomNotReadyMenuOptions () {
            let customNotReadyStateOptions = [];
            if (!(notReadyReasonCodesRef.current || notReadyReasonCodesRef.current.length)) {
                return customNotReadyStateOptions;
            }
            customNotReadyStateOptions = notReadyReasonCodesRef.current.map((reasonCode) => (
                {
                    menuId: `${AGENT_NON_VOICE_STATES.NOT_READY}_${reasonCode.id}`,
                    label: reasonCode.label,
                    status: channelService.STATE_STATUS.UNAVAILABLE,
                }
            ));
            return customNotReadyStateOptions;
        }

        function getDefaultChannelStates () {
            let channelStates = [];
            channelStates.push({...CHANNEL_STATE_MAP.get(window.finesse.restservices.Media.States.READY)});
            channelStates.push({...CHANNEL_STATE_MAP.get(window.finesse.restservices.Media.States.NOT_READY)});
            return channelStates;
        }

        function getAllChannelStates () {
            let defaultChannelStates =  getDefaultChannelStates();
            let customNotReadyStates = getCustomNotReadyMenuOptions();
            if (customNotReadyStates.length) {
                defaultChannelStates = defaultChannelStates.filter((state) => state.menuId !== NOT_READY_MENU_ID);
            }
            return [...defaultChannelStates, ...customNotReadyStates];

        }

        function getMediaObj (id) {
            const mediaObj = allMediaObjects.current.find((media) => media.getId() === id);
            return mediaObj;
        }

        function getMediaDomain (id) {
            const mediaDomainObj = mediaDomainList.current.find((media) => media.id === id);
            return mediaDomainObj;
        }

        function getChatChannelData (mediaId) {
            const channelStates = [...getAllChannelStates()];
            const channelType = getChannelType(mediaId);
            const updatedChannelDataProps = getChannelDataProps(channelType);

            return {
                channelId: `${mediaId}`,
                label: CHAT_LABEL,
                icon: CHAT_ICON_NAME,
                states: updatedChannelDataProps.states, // channelStates,
                enable: updatedChannelDataProps.enable,
                curStateMenuId: updatedChannelDataProps.curStateMenuId,
                curStateLabel: updatedChannelDataProps.curStateLabel,
                curStateStatus: updatedChannelDataProps.curStateStatus,
                logoutDisabled: updatedChannelDataProps.logoutDisabled,
                iconBadge: channelService.ICON_BADGE_TYPE.NONE,
                iconColor: updatedChannelDataProps.iconColor,
                hoverText: updatedChannelDataProps.hoverText,
                allowStateChange: true,
                isPopDisplayed: false,
                popOverId: '',
                channelType: CHANNEL_TYPES.CHAT,
                hoverTextPrefixDisabled: updatedChannelDataProps.hoverTextPrefixDisabled
            };
        }

        function getEmailChannelData (mediaId) {
            const channelStates = [...getAllChannelStates()];
            const channelType = getChannelType(mediaId);
            const updatedChannelDataProps = getChannelDataProps(channelType);

            return {
                channelId: `${mediaId}`,
                label: EMAIL_LABEL,
                icon: EMAIL_ICON_NAME,
                states: updatedChannelDataProps.states, // channelStates,
                enable: updatedChannelDataProps.enable,
                curStateMenuId: updatedChannelDataProps.curStateMenuId,
                curStateLabel: updatedChannelDataProps.curStateLabel,
                curStateStatus: updatedChannelDataProps.curStateStatus,
                logoutDisabled: updatedChannelDataProps.logoutDisabled,
                iconBadge: channelService.ICON_BADGE_TYPE.NONE,
                iconColor: updatedChannelDataProps.iconColor,
                hoverText: updatedChannelDataProps.hoverText,
                allowStateChange: true,
                isPopDisplayed: false,
                popOverId: '',
                channelType: CHANNEL_TYPES.EMAIL,
                hoverTextPrefixDisabled: updatedChannelDataProps.hoverTextPrefixDisabled
            };
        }

        function getStateChangeErrorResponse (msg) {
            return {
                status: channelService.STATUS.FAILURE,
                error: {
                    errorCode: '567',
                    errorDesc: msg
                }
            };
        }

        function log (msg) {
            msg = 'Manage Digital Channel Gadget: ' + msg;
            console.log(msg);
        }

        function getChannelMenuPayload (menuLabel, channelStates) {
            const menuItems = channelStates.map((state) => (
                {
                    id: state.menuId,
                    label: state.label,
                    iconColor: state.menuId === READY_MENU_ID ? channelService.STATE_STATUS.AVAILABLE : channelService.STATE_STATUS.UNAVAILABLE
                }
            ));

            return {
                label: menuLabel,
                menuItems: menuItems
            };
        }

        function getChannelConfigPayload (icon) {
            return {
                actionTimeoutInSec: ACTION_TIMEOUT_SECONDS,
                icons: [{
                    type: channelService.ICON_TYPE.COLLAB_ICON,
                    value: icon
                }]
            };
        }

        function getChannelStatePayload (channelState) {
            return {
                label: channelState.label,
                currentState: channelState.curStateLabel,
                iconColor: channelState.iconColor,
                enable: channelState.enable,
                logoutDisabled: channelState.logoutDisabled,
                iconBadge: channelState.iconBadge || channelService.ICON_BADGE_TYPE.NONE,
                hoverText: channelState.hoverText,
                hoverTextPrefixDisabled: channelState.hoverTextPrefixDisabled,
                channelType: channelState.channelType
            };
        }

        function updateChannel (channelData, onSuccess, onFailure) {
            let channelStateData = getChannelStatePayload(channelData);
            let menuConfigData = getChannelMenuPayload(channelData.label, channelData.states);
            let payload = {
                channelState: channelStateData,
                menuConfig: menuConfigData
            };
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [updateChannel] : Update Channel Id: ' + channelData.channelId + ', Payload: ' + JSON.stringify(payload));

            channelService.updateChannel(channelData.channelId, payload, onSuccess, onFailure);
        }

        function updateFncChannelData (channelType) {
            if ((channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) && !registeredChannelsRef.current.chat) {
                return;
            } else if ((channelType === CHANNEL_TYPES.EMAIL) && !registeredChannelsRef.current.email) {
                return;
            }
            let channelData = (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) ? chatChannelData.current : emailChannelData.current;
            const updatedChannelDataProps = getChannelDataProps(channelType);
            channelData = {...channelData, ...updatedChannelDataProps};

            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [updateFncChannelData] : Updating channel with channelData: ', JSON.stringify(channelData));
            updateChannel(channelData);
        }

        function getAllMediaIdsOfChannel(channelType) {
            if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                return allChannelIds.current[CHANNEL_TYPES.CHAT].concat(allChannelIds.current[CHANNEL_TYPES.SOCIAL]);
            } else if(channelType === CHANNEL_TYPES.EMAIL) {
                return allChannelIds.current[channelType];
            }
        }

        function setNotRoutableMediaIdsPostRona (mediaIdsList, channelType, setType) {
            if (setType === 'reset') {
                if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                    fncNonRoutableMediaIds.current[CHANNEL_TYPES.CHAT] = [];
                    fncNonRoutableMediaIds.current[CHANNEL_TYPES.SOCIAL] = [];
                } else if(channelType === CHANNEL_TYPES.EMAIL) {
                    fncNonRoutableMediaIds.current[channelType] = [];
                }
                return;
            }
            mediaIdsList.forEach((mediaId) => {
                const channelType = getChannelType(mediaId);
                if (channelType && !fncNonRoutableMediaIds.current.hasOwnProperty(channelType)) {
                    fncNonRoutableMediaIds.current[channelType] = [];
                }
                channelType && fncNonRoutableMediaIds.current[channelType].push(mediaId);
            });
        }

        function getReasonCodeFromSelectedMenuId (selectedMenuId) {
            let reasonCode;
            if (selectedMenuId.startsWith(`${AGENT_NON_VOICE_STATES.NOT_READY}_`)) {
                let reasonCodeId = selectedMenuId.split(`${AGENT_NON_VOICE_STATES.NOT_READY}_`)[1];
                reasonCode = findNotReadyReasonCodeById(reasonCodeId);
            }
            return reasonCode;
        }

        async function setMediaState (channelId, selectedMenuId, onSuccess, onError, isNonRoutableCase = false, setStateFromCache = false, isRestorePostFailover = false) {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] : args : ', channelId, selectedMenuId, onSuccess, onError, isNonRoutableCase, setStateFromCache);

            const mediaId = channelId;
            const channeltype = getChannelType(mediaId);
            const setStateMediaIds = getAllMediaIdsOfChannel(channeltype);
            if (!setStateMediaIds) {
                return;
            }
            let newState = selectedMenuId === READY_MENU_ID ? AGENT_NON_VOICE_STATES.READY : AGENT_NON_VOICE_STATES.NOT_READY;
            let reasonCode = getReasonCodeFromSelectedMenuId(selectedMenuId);

            function handleStateChangeSuccess (setStateresults) {
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] : Atleast one state change passed. setStateresults: ', JSON.stringify(setStateresults));

                if (isNonRoutableCase || setStateFromCache) {
                    onSuccess(setStateresults);

                    if (isNonRoutableCase) {
                        //cache the media ids that are made NOT_READY post rona. Id's will be later used for fnc channel hovertext
                        const stateChangePassedMediaIds = setStateresults.filter((res) => res.status === 'fulfilled').map((res) => res.value.mediaId);
                        setNotRoutableMediaIdsPostRona(stateChangePassedMediaIds, channeltype, '');
                    }
                } else {
                    onSuccess({
                        channelId: channelId,
                        status: channelService.STATUS.SUCCESS
                    });

                    // reset the media Ids if state change happens through non-voice state control gadget 
                    setNotRoutableMediaIdsPostRona([], channeltype, 'reset');
                }
                updateFncChannelData(channeltype);

            }
    
            function handleStateChangeError (msg, setStateresults) {
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] : All state change failed. setStateresults: ', msg, JSON.stringify(setStateresults));

                let errorPayload = getStateChangeErrorResponse(msg);
                errorPayload.channelId = channelId;
                if (isNonRoutableCase || setStateFromCache) {
                    onError(setStateresults);
                } else {
                    onError(errorPayload);
                }
            }

            function getHandlers (id, requestType, reasonCode, resolve, reject) {
                return {
                    success: function (msg) {
                        console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] : state change success: ', msg, JSON.stringify({mediaId: id, requestType, reasonCode, msg}));
                        resolve({mediaId: id, requestType, reasonCode, msg});
                    },
                    error: function (err) {
                        console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] :state change error because of E_ARM_STAT_ALREADY_IN_REQUESTED_AGENT_STATE. Hence marking as success', err, {mediaId: id, requestType, reasonCode, err});
                        if (err.object?.ApiErrors?.ApiError?.ErrorMessage === 'E_ARM_STAT_ALREADY_IN_REQUESTED_AGENT_STATE') {
                            resolve({mediaId: id, requestType, reasonCode, err});
                        } else {
                            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] : state change error: marking as failure', err, JSON.stringify({mediaId: id, requestType, reasonCode, err}));

                            reject({mediaId: id, requestType, reasonCode, err});
                        }
                    }
                }
            }

            function getMakeRequestType (id) {
                const mediaObj = getMediaObj(id);
                const curMediaState = mediaObj.getState();
                if (setStateFromCache) {
                    newState = getCachedMediaState(id);
                    if (!newState) {
                        return;
                    }
                    reasonCode = getReasonCodeFromSelectedMenuId(newState);
                    if (newState.startsWith(`${AGENT_NON_VOICE_STATES.NOT_READY}_`)) {
                        newState = AGENT_NON_VOICE_STATES.NOT_READY;
                    }
                    if (newState === AGENT_NON_VOICE_STATES.READY) {
                        return 'setState';
                    }
                    // on failover, desktop connected api will restore mrd back to ACTIVE. no need to make READY req 
                    if (newState === 'ACTIVE' && !isRestorePostFailover) {
                        newState = AGENT_NON_VOICE_STATES.READY;
                        return 'setState';
                    }
                }

                if (!mediaObj) {
                    return false;
                }
                if (!mediaObj.isLoggedIn() || !curMediaState) {
                    return false;
                }
                if (newState === AGENT_NON_VOICE_STATES.READY) {
                    // check if current media state is not_ready
                    if (curMediaState && curMediaState === AGENT_NON_VOICE_STATES.NOT_READY) return 'setState';

                    // check if current media state is one among the ready states(READY, RESERVED, WORK, INTERRUPTED, ACTIVE etc) and is not_routable
                    if (curMediaState && curMediaState !== AGENT_NON_VOICE_STATES.NOT_READY && !mediaObj.isRoutable()) return 'setRoutable';
                    return false;
                }
                if (newState === AGENT_NON_VOICE_STATES.NOT_READY) {
                    if (curMediaState && curMediaState !== AGENT_NON_VOICE_STATES.NOT_READY) return 'setState';

                    const curMediaReasonCodeId = mediaObj.getReasonCodeId();

                    // selected option is custom NOT_READY code
                    if (reasonCode) {
                        if (curMediaState === AGENT_NON_VOICE_STATES.NOT_READY && curMediaReasonCodeId !== reasonCode.id) return 'setState';
                        return false;
                    } else {    // selected option is simple NOT_READY
                        if (curMediaState === AGENT_NON_VOICE_STATES.NOT_READY) return false;
                        return 'setState';
                    }
                }
            }

            const setStateresults = await Promise.allSettled(setStateMediaIds.map((id) => {
                let mediaObj = getMediaObj(id);
                const requestType = getMakeRequestType(id);

                if (requestType && requestType === 'setState') {
                    if (newState === AGENT_NON_VOICE_STATES.NOT_READY && isRestorePostFailover) {
                        return new Promise((resolve, reject) => {
                            mediaObj.setState('', undefined, {
                                mediaConnected: true,
                                handlers: getHandlers(id, requestType, undefined, resolve, reject)
                            });
                        });
                    } else {
                        return new Promise((resolve, reject) => {
                            mediaObj.setState(newState, reasonCode, {
                                handlers: getHandlers(id, requestType, reasonCode, resolve, reject)
                            });
                        });
                    }
                } else if (requestType && requestType === 'setRoutable') {
                    return new Promise((resolve, reject) => {
                        mediaObj.setRoutable({
                            routable: true,
                            handlers: getHandlers(id, requestType, reasonCode, resolve, reject)
                        });
                    });
                }
            }).filter((mediaReq) => mediaReq)
            );
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaState] : final setStateresults ', JSON.stringify(setStateresults));
            const isAllRejected = setStateresults.every((res) => res.status === 'rejected');
            const stateChangeFailedChannels = setStateresults.filter((res) => res.status === 'rejected').map((res) => getMediaType(res.reason.mediaId));

            if (!setStateresults.length || !isAllRejected) {
                handleStateChangeSuccess(setStateresults);
            } else if (isAllRejected) {
                handleStateChangeError(`State Change Failed - ${stateChangeFailedChannels.join(', ')}`, setStateresults);
            }

            if (stateChangeFailedChannels.length && !isAllRejected) {
                let label = '';
                if (channeltype === CHANNEL_TYPES.CHAT || channeltype === CHANNEL_TYPES.SOCIAL) {
                    label = CHAT_LABEL;
                } else if(channeltype === CHANNEL_TYPES.EMAIL) {
                    label = EMAIL_LABEL;
                }
                ImiDesktopNotificationManager.showNotification(ACTIONS.STATE_CHANGE_FAILED, label, stateChangeFailedChannels.join(', '));
            }
        }

        function menuHandler (channelId, selectedMenuId, onSuccess, onError) {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [menuHandler] : ' + 'Menu Selection Request received from channel id:  ' + channelId + ', Payload: ' + selectedMenuId);

            if (selectedMenuId) {
                let channelData = channelId === `${chatChannelId.current}` ? chatChannelData.current : emailChannelData.current;
                // ALL menu handling
                if (selectedMenuId.toUpperCase() === 'ALL_READY') {
                    selectedMenuId = CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.READY).menuId; // channelData.states.find((state) => state.menuId === READY_MENU_ID).menuId;
                } else if (selectedMenuId.toUpperCase() === 'ALL_NOT_READY') {
                    selectedMenuId = CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.NOT_READY).menuId; // channelData.states.find((state) => state.menuId === NOT_READY_MENU_ID).menuId;
                }
                setMediaState(channelId, selectedMenuId, onSuccess, onError, false, false, false);

                // the timeout to reset recoveredref is 10000ms(in fin-imi/index.js). So, if agent changes state post failback(before timeout value). update the cache.
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [menuHandler] : recovered :  ' + recoveredRef.current);
                recoveredRef.current && debouncedCacheMediaStatesDuringRestoring();
            }
        }

        function addChannel (channelData, onSuccess, onFailure) {

            let menuConfigData = getChannelMenuPayload(channelData.label, channelData.states);
            let channelConfigData = getChannelConfigPayload(channelData.icon);
            let channelStateData = getChannelStatePayload(channelData);

            let data = {
                menuConfig: menuConfigData,
                channelConfig: channelConfigData,
                channelState: channelStateData
            };
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [addChannel] : Add Channel Data: ', JSON.stringify(data));

            channelService.addChannel(channelData.channelId, data, menuHandler,
                onSuccess, onFailure);
        }

        function removeChannel (channelId, onSuccess, onFailure) {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [removeChannel] : Remove Channel Id: ' + channelId);

            channelService.removeChannel(channelId, onSuccess, onFailure);
        }

        function addChannels () {
            if (chatChannelId.current) {
                chatChannelData.current = getChatChannelData(chatChannelId.current);
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [addAllChannels] : chatChanneldata : ', JSON.stringify(chatChannelData.current));
                registeredChannelsRef.current.chat = true;
                addChannel(chatChannelData.current);
            }
            if (emailChannelId.current) {
                emailChannelData.current = getEmailChannelData(emailChannelId.current);
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [addAllChannels] : emailChannelData : ', JSON.stringify(emailChannelData.current));
                registeredChannelsRef.current.email = true;
                addChannel(emailChannelData.current);
            }
        }

        function addAllChannels () {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [addAllChannels] : Starting to add all channels.');

            // loop to push all valid media id's to an array before addChannel
            for (let media of mediaList.current) {
                // MRD ID 1 is voice media, so ignoring that as per the requirement
                const mediaDomain = getMediaDomain(media.id);
                if (mediaDomain && mediaDomain.finesseAssociated === 'true' && media.id !== '1' && mediaDomain.channel && mediaDomain.channel.toLowerCase() !== 'unknown' && mediaDomain.mediaClass && mediaDomain.mediaClass.toLowerCase() !== 'unknown') {
                    let channelType = getChannelType(media.id);
                    if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                        allChannelIds.current[channelType].push(media.id);
                        if (!chatChannelId.current) {
                            chatChannelId.current = media.id;
                        }
                    } else if (channelType === CHANNEL_TYPES.EMAIL) {
                        allChannelIds.current[channelType].push(media.id);
                        if (!emailChannelId.current) {
                            emailChannelId.current = media.id;
                        }
                    }
                }
            }

            // restore mrd states afer failover
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [addAllChannels] : CACHED_FINESSE_NON_VOICE_SIGNED_IN : ${CACHED_FINESSE_NON_VOICE_SIGNED_IN}`, window.top.finesse.container.Config.hasFailedover && CACHED_FINESSE_NON_VOICE_SIGNED_IN);

            if (window.top.finesse.container.Config.hasFailedover && CACHED_FINESSE_NON_VOICE_SIGNED_IN) {
                let shouldRestoreMediaStates = allMediaObjects.current.find((mediaObj) => {
                    console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [addAllChannels] : shouldRestoreMediaStates loop... : `, mediaObj, mediaObj.getState(), mediaObj.getState() === AGENT_NON_VOICE_STATES.WORK);

                    return mediaObj.getState() === AGENT_NON_VOICE_STATES.WORK
                });
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [addAllChannels] : shouldRestoreMediaStates : ${shouldRestoreMediaStates}`);

                if (shouldRestoreMediaStates) {
                    INITIAL_PROPS.setMediaRestoring(true);
                    restoreAfterFailoverInProgress.current = true;
                    restoreMediaStatespostFailover(() => {
                        // Callback to add channels after state restored post failover. settimeout to media synchronize
                        setTimeout(() => {
                            addChannels();
                            INITIAL_PROPS.setMediaRestoring(false);
                            restoreAfterFailoverInProgress.current = false;
                        }, 500);
                    });
                } else {
                    addChannels();
                    restoreAfterFailoverInProgress.current = false;
                }
            } else {
                addChannels();
                restoreAfterFailoverInProgress.current = false;
            }
        }

        function initialize(mediaListData, mediaDomainListData, allMediaObject, notReadyReasonCodes) {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [initialize] : args : ', mediaListData, mediaDomainListData, allMediaObject, notReadyReasonCodes);

            if (!(mediaListData && mediaDomainListData && allMediaObject)) {
                throw new Error('Invalid arguments supplied to useDigitalChannelSetup: Initialize method.');
            }
            logger = window.finesse.cslogger.ClientLogger;
            logger.init(window.gadgets.Hub, 'DigitalChannelsGadget');
            window.finesse.clientservices.ClientServices.setLogger(logger);

            mediaList.current = mediaListData;
            mediaDomainList.current = mediaDomainListData;
            allMediaObjects.current = allMediaObject;
            notReadyReasonCodesRef.current = notReadyReasonCodes;
            
            // make all signedIn medias as routable if incase the media is non_routable
            setMediaRoutable(true);
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [initialize] : cached items before calling cachemediastates() onLoad : ', getAllCachedMediaStatesObj(), getFromSessionStorage(FINESSE_NON_VOICE_SIGNED_IN_KEY), window.top.finesse.container.Config.hasFailedover, window.top.finesse.container.Config.isFailingOver);

            //cache all media states on init
            if (!window.top.finesse.container.Config.hasFailedover) {
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [initialize] : hasFailedover is false and therefore calling cachemediastates() onLoad. ');

                cacheMediaStates();
            }
        }

        function restoreMediaStatespostFailover (callback) {
            let totalChannnels = 0, restoredChannels = 0;
            if (chatChannelId.current) {
                totalChannnels += 1;
            }
            if (emailChannelId.current) {
                totalChannnels += 1;
            }
            function onSuccess (res) {
                
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreMediaStatespostFailover] : made NOT_READY for ${res.channelId} channel and now making cache request`, res);
                let ignoreChatRestore = false, ignoreEmailRestore = false;
                if (res.channelId === chatChannelId.current) {
                    ignoreChatRestore = false;
                    ignoreEmailRestore = true;
                } else if (res.channelId === emailChannelId.current) {
                    ignoreChatRestore = true;
                    ignoreEmailRestore = false;
                }
                restoreFncStatesFromCache(ignoreChatRestore, ignoreEmailRestore, {
                    onSuccess: (setStateresults) => {
                        restoredChannels += 1;
                        if (restoredChannels === totalChannnels) { // to ensure callback is called only after both chat and email requests are completed
                            callback();
                        }
                    },
                    onError: (setStateresults) => {
                        restoredChannels += 1;
                        if (restoredChannels === totalChannnels) {
                            callback();
                        }
                    }
                }, true);
            }

            function onError (res) {
                restoredChannels += 1;
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreMediaStatespostFailover] : state change failed : ', res);
                callback();
            }
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreMediaStatespostFailover] : making NOT_READY for chat and email channels`);

            if (chatChannelId.current) {
                setMediaState(chatChannelId.current, NOT_READY_MENU_ID, onSuccess, onError, false, false, true);
            }
            if (emailChannelId.current) {
                setMediaState(emailChannelId.current, NOT_READY_MENU_ID, onSuccess, onError, false, false, true);
            }

            if (!chatChannelId.current && !emailChannelId.current) {
                callback();
            }
        }

        function getSignInFailedMediaIds(channelType) {
            updateMediaSignInStatus();
            return Object.keys(channelsSignInStatus.current).filter((mediaId) => (getChannelType(mediaId) === channelType) && channelsSignInStatus.current[mediaId] === 'failure');
        }

        function getSignInFailedChannelsTitle(channelType) {
            const signInFailedMediaIds = getSignInFailedMediaIds(channelType);
            return signInFailedMediaIds.map((mediaId) => {
                const mediaType = getMediaType(mediaId);
                return mediaType;
            });
        }

        function getReadyAndNotReadyMediasNames(channelType) {
            const allMediaIdsList = allChannelIds.current[channelType];
            let mediaNamesWithState = {};
            const readyLocalString = I18n.getString('desktop.agent.header.state.' + AGENT_NON_VOICE_STATES.READY);
            const notReadyLocalString = I18n.getString('desktop.agent.header.state.' + AGENT_NON_VOICE_STATES.NOT_READY);
            const notReadyDueToUnavailabilityLocalString = i18n.getMsg('gadget.imi.desktopNotification.AllNotReadyDueToAgentUnavailability.desc');
            const nonRoutableMediaIds = fncNonRoutableMediaIds.current[channelType] || [];

            allMediaIdsList.forEach((mediaId) => {
                const mediaObj = getMediaObj(mediaId);

                if (isMediaInReadyState(mediaObj)) {
                    const mediaType = getMediaType(mediaId);
                    if (!mediaNamesWithState.hasOwnProperty(readyLocalString)) {
                        mediaNamesWithState[readyLocalString] = []
                    }
                    mediaNamesWithState[readyLocalString].push(mediaType);
                } else if (isMediaInNotReadyState(mediaObj)) {
                    const mediaType = getMediaType(mediaId);
                    const reasonCodeLabel = mediaObj.getData()?.reasonCode?.label;
                    let key = reasonCodeLabel ? `${notReadyLocalString}(${reasonCodeLabel})` : notReadyLocalString;
                    if (nonRoutableMediaIds.includes(mediaId)) {
                        key = notReadyDueToUnavailabilityLocalString;
                    }
                    if (!mediaNamesWithState.hasOwnProperty('ALL_NOT_READY_MEDIA_LIST')) {
                        mediaNamesWithState['ALL_NOT_READY_MEDIA_LIST'] = [];
                    }
                    if (!mediaNamesWithState.hasOwnProperty('NOT_READY_DUE_TO_NOT_ROUTABLE_MEDIA_LIST')) {
                        mediaNamesWithState['NOT_READY_DUE_TO_NOT_ROUTABLE_MEDIA_LIST'] = [];
                    }
                    if (!mediaNamesWithState.hasOwnProperty(key)) {
                        mediaNamesWithState[key] = [];
                    }
                    mediaNamesWithState[key].push(mediaType);
                    mediaNamesWithState['ALL_NOT_READY_MEDIA_LIST'].push(mediaType);
                    nonRoutableMediaIds.includes(mediaId) && mediaNamesWithState['NOT_READY_DUE_TO_NOT_ROUTABLE_MEDIA_LIST'].push(mediaType);
                }
            });
            return mediaNamesWithState ;
        }

        function isMediaInReadyState (mediaObj) {
            if (!mediaObj) return;
            return mediaObj.isLoggedIn() && (mediaObj.getState() && mediaObj.getState() !== AGENT_NON_VOICE_STATES.NOT_READY) && mediaObj.isRoutable();
        }

        // when media has state as READY but NOT_ROUTABLE, it is considered as NOT_READY
        function isMediaInNotReadyState (mediaObj) {
            if (!mediaObj) return;
            return mediaObj.isLoggedIn() && (mediaObj.getState() && mediaObj.getState() === AGENT_NON_VOICE_STATES.NOT_READY || (mediaObj.getState() && mediaObj.getState() !== AGENT_NON_VOICE_STATES.NOT_READY && !mediaObj.isRoutable()));
        }

        function updateMediaSignInStatus() {
            allMediaObjects.current.forEach((media) => {
                if (channelsSignInStatus.current.hasOwnProperty(media.getId()) && media.isLoggedIn()) {
                    channelsSignInStatus.current[media.getId()] = 'success';
                } else if (channelsSignInStatus.current.hasOwnProperty(media.getId()) && !media.isLoggedIn()) {
                    channelsSignInStatus.current[media.getId()] = 'failure';
                }
            });
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [updateMediaSignInStatus] : SignIn status object after updating : ', JSON.stringify(channelsSignInStatus.current));
        }

        function hasChannelSignedIn(channelType) {
            return checkAllMediaStatesEqual(channelType, AGENT_NON_VOICE_STATES.LOGIN);
        }

        function handleSigninFailure (mediaId) {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [handleSigninFailure] : SignIn failed for : ' + mediaId);

            channelsSignInStatus.current[mediaId] = 'failure';

            const channelType = getChannelType(mediaId);

            if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                debouncedFncChatUpdate(channelType);
            } else if (channelType === CHANNEL_TYPES.EMAIL) {
                debouncedFncEmailUpdate(channelType);
            }
        }

        function handleSigninSucces (mediaId) {
            channelsSignInStatus.current[mediaId] = 'success';

            const channelType = getChannelType(mediaId);
        }

        function handleMediaChange (media) {
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleMediaChange] : media change event received for : ${media.getId()}, state : ${media.getState()}, routable: ${media.isRoutable()}, recovered : ${recoveredRef.current} and restoreAfterFailoverInProgress : ${restoreAfterFailoverInProgress.current}`);

            const mediaId = media.getId();
            const channelType = getChannelType(mediaId);
            if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                debouncedFncChatUpdate(channelType);
            } else if(channelType === CHANNEL_TYPES.EMAIL) {
                debouncedFncEmailUpdate(channelType);
            }

            if (recoveredRef.current && media.getState() !== AGENT_NON_VOICE_STATES.NOT_READY && !media.isRoutable()) {
                mediaIdsOfRonaDuringReconnecting.current.push(mediaId);
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleMediaChange] : NOT_ROUTABLE on reconnecting. hence adding to array : `, mediaIdsOfRonaDuringReconnecting.current);

            } else if (!restoreAfterFailoverInProgress.current && media.getState() !== AGENT_NON_VOICE_STATES.NOT_READY && !media.isRoutable()) {
                setTimeout((media) => {
                    console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleMediaChange] : timeout- media change event received for : ${media.getId()}, state : ${media.getState()}, routable: ${media.isRoutable()} and recovered : ${recoveredRef.current}`);
    
                    if (media.getState() !== AGENT_NON_VOICE_STATES.NOT_READY && !media.isRoutable()) {
                        handleNonRoutableEvent(media);
                    }
                }, 200, media);
            }
            // save the media state in sessionstorage. will be used to restore fnc states after failback
            if (!recoveredRef.current && !restoreAfterFailoverInProgress.current) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleMediaChange] : caching all mrd's`);

                cacheMediaStates();
            } else {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleMediaChange] : media Id : ${media.getId()}, hasToCache: ${isMediaInReadyState(media)}`);
                // refer: CSCwe50645
                // if recovery is true and state changed to READY(for example: media state restoring post recovery). update only the particular mrd in cache.
                isMediaInReadyState(media) && cacheMediaStates([media]);
            }
        }

        function checkAllMediaStatesEqual (channelToBeChecked, stateToBeChecked) {
            let result, channelIdsList = allChannelIds.current[channelToBeChecked] || [];
            switch(stateToBeChecked) {
                case AGENT_NON_VOICE_STATES.LOGOUT:
                    result = channelIdsList.filter((channelId) => {
                        const mediaObj = getMediaObj(channelId);
                        return !mediaObj.isLoggedIn();
                    });
                    break;
                case AGENT_NON_VOICE_STATES.LOGIN:
                    result = channelIdsList.filter((channelId) => {
                        const mediaObj = getMediaObj(channelId);
                        return mediaObj.isLoggedIn();
                    });
                    break;
                case AGENT_NON_VOICE_STATES.READY:
                    result = channelIdsList.filter((channelId) => {
                        const mediaObj = getMediaObj(channelId);
                        return isMediaInReadyState(mediaObj);
                    });
                    break;
                case AGENT_NON_VOICE_STATES.NOT_READY:
                    result = channelIdsList.filter((channelId) => {
                        const mediaObj = getMediaObj(channelId);
                        return isMediaInNotReadyState(mediaObj);
                    });
                    break;
                default:
                    result = channelIdsList.filter((channelId) => {
                        const mediaObj = getMediaObj(channelId);
                        return mediaObj.getState() === stateToBeChecked;
                    });
                    break;
            }
            return result.length === allChannelIds.current[channelToBeChecked].length;
        }

        function setMediaRoutable (routable, mediaList) {
            const list = mediaList || allMediaObjects.current;
            list.forEach((media) => {
                if (media.isLoggedIn() && media.getRoutable() !== routable) {
                    console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaRoutable] : making routable:${routable} for : ${media.getId()}, recovered : ${recoveredRef.current}`);

                    media.setRoutable({
                        routable: routable,
                        handlers: {
                            success: () => {
                                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaRoutable] : Routable made ${routable} for : ${media.getId()}, recovered : ${recoveredRef.current}`);
                            },
                            error: () => {
                                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [setMediaRoutable] : unable to make Routable ${routable} for : ${media.getId()}, recovered : ${recoveredRef.current}`);
                            }
                        }
                    });
                } 
            });
        }

        function getChannelLabel (channelType) {
            let label;
            if (channelType && (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL)) {
                label = CHAT_LABEL
            } else if(channelType && channelType === CHANNEL_TYPES.EMAIL) {
                label = EMAIL_LABEL
            }
            return label;
        }

        function getFilteredChannelStates (channelType) {
            let channelStates = [...getAllChannelStates()], allMediaIdsList = [], readyCount = 0, notReadyCount = 0, customNotReadyMenuIds = [];
            if (channelType && (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL)) {
                allMediaIdsList = allChannelIds.current[CHANNEL_TYPES.CHAT].concat(allChannelIds.current[CHANNEL_TYPES.SOCIAL]);
            } else if(channelType && channelType === CHANNEL_TYPES.EMAIL) {
                allMediaIdsList = allChannelIds.current[channelType] || [];
            }
            allMediaIdsList.forEach((mediaId) => {
                const mediaObj = getMediaObj(mediaId);

                if (isMediaInReadyState(mediaObj)) {
                    readyCount += 1;
                } else if (isMediaInNotReadyState(mediaObj)) {
                    notReadyCount += 1;
                    const reasonCode = mediaObj.getData()?.reasonCode;
                    if (reasonCode && !customNotReadyMenuIds.includes(`${AGENT_NON_VOICE_STATES.NOT_READY}_${reasonCode.id}`)) {
                        customNotReadyMenuIds.push(`${AGENT_NON_VOICE_STATES.NOT_READY}_${reasonCode.id}`);
                    }
                }
            });
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getFilteredChannelStates] : post loop : `, channelType, allMediaIdsList, readyCount, notReadyCount, customNotReadyMenuIds);

            // In Partial Ready condition
            if (readyCount && notReadyCount) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getFilteredChannelStates] : channelStates : `, channelStates);

                return channelStates;
            }

            if (readyCount === allMediaIdsList.length) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getFilteredChannelStates] : readyCount === allMediaIdsList.length : `);

                // if all channels are in READY state, then remove Ready option from fnc dropdown
                channelStates = channelStates.filter((state) => state.menuId !== READY_MENU_ID);
            } else if (notReadyCount === allMediaIdsList.length) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getFilteredChannelStates] : notReadyCount === allMediaIdsList.length : `);

                channelStates = channelStates.filter((state) => state.menuId !== NOT_READY_MENU_ID && !customNotReadyMenuIds.includes(state.menuId));
            }
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getFilteredChannelStates] : channelStates : `, channelStates);

            return channelStates;
        }

        function getChannelDataProps(channelType) {
            const channelData = {
                hoverText: '',
                curStateMenuId: '',
                curStateLabel: '',
                curStateStatus: '',
                hoverTextPrefixDisabled: false,
                iconColor: channelService.STATE_STATUS.UNAVAILABLE,
                enable: true,
                logoutDisabled: false,
                states: getFilteredChannelStates(channelType)
            };
            const readyLocalString = I18n.getString('desktop.agent.header.state.' + AGENT_NON_VOICE_STATES.READY);

            let signInFailedChannelTitle, allMediaIdsList, areAllMediasSignedIn, areMediasPartialSignedIn, hasAllMediaLoggedOut, readyAndNotReadyMediasNames = {};

            if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                allMediaIdsList = allChannelIds.current[CHANNEL_TYPES.CHAT].concat(allChannelIds.current[CHANNEL_TYPES.SOCIAL]);
                areAllMediasSignedIn = hasChannelSignedIn(CHANNEL_TYPES.CHAT) && hasChannelSignedIn(CHANNEL_TYPES.SOCIAL);
                hasAllMediaLoggedOut = !areAllMediasSignedIn && checkAllMediaStatesEqual(CHANNEL_TYPES.CHAT, AGENT_NON_VOICE_STATES.LOGOUT) && checkAllMediaStatesEqual(CHANNEL_TYPES.SOCIAL, AGENT_NON_VOICE_STATES.LOGOUT);
                signInFailedChannelTitle = getSignInFailedChannelsTitle(CHANNEL_TYPES.CHAT).concat(getSignInFailedChannelsTitle(CHANNEL_TYPES.SOCIAL));
                if (!hasAllMediaLoggedOut) {
                    const chat_readyAndNotReadyMediasNames = getReadyAndNotReadyMediasNames(CHANNEL_TYPES.CHAT);
                    const social_readyAndNotReadyMediasNames = getReadyAndNotReadyMediasNames(CHANNEL_TYPES.SOCIAL);
                    const allStates = [...Object.keys(chat_readyAndNotReadyMediasNames), ...Object.keys(social_readyAndNotReadyMediasNames)];
                    [...new Set(allStates)].forEach((state) => {
                        readyAndNotReadyMediasNames[state] = [];
                        if (chat_readyAndNotReadyMediasNames.hasOwnProperty(state)) {
                            readyAndNotReadyMediasNames[state] = [...readyAndNotReadyMediasNames[state], ...chat_readyAndNotReadyMediasNames[state]];
                        }
                        if (social_readyAndNotReadyMediasNames.hasOwnProperty(state)) {
                            readyAndNotReadyMediasNames[state] = [...readyAndNotReadyMediasNames[state], ...social_readyAndNotReadyMediasNames[state]];
                        }
                    });
                }
            } else if (channelType === CHANNEL_TYPES.EMAIL) {
                allMediaIdsList = allChannelIds.current[channelType] || [];
                areAllMediasSignedIn = hasChannelSignedIn(channelType);
                hasAllMediaLoggedOut = !areAllMediasSignedIn && checkAllMediaStatesEqual(channelType, AGENT_NON_VOICE_STATES.LOGOUT);
                signInFailedChannelTitle = getSignInFailedChannelsTitle(channelType);
                readyAndNotReadyMediasNames = !hasAllMediaLoggedOut ? getReadyAndNotReadyMediasNames(channelType) : {};
            }

            function setOtherProps() {
                channelData.enable = hasAllMediaLoggedOut ? false : true;
            }

            function setFncChannelHoverText() {
                    if (hasAllMediaLoggedOut) {
                        channelData.hoverText = signInFailedChannelTitle.length === allMediaIdsList.length ? i18n.getMsg('gadget.imi.fnc.text.signInFailed') : I18n.getString('desktop.agent.header.state.' + AGENT_NON_VOICE_STATES.LOGOUT);
                    } else {
                        let channelState; 
                        Object.keys(readyAndNotReadyMediasNames).forEach((state) => {
                            if (state !== 'ALL_NOT_READY_MEDIA_LIST' && readyAndNotReadyMediasNames[state] && readyAndNotReadyMediasNames[state].length === allMediaIdsList.length) {
                                channelState = state;
                            }
                        });

                        if (channelState) {
                            channelData.hoverText = channelState;
                            if (channelState === 'NOT_READY_DUE_TO_NOT_ROUTABLE_MEDIA_LIST') {
                                channelData.hoverText = `${i18n.getMsg('gadget.imi.desktopNotification.AllNotReadyDueToAgentUnavailability.desc')}`;
                            }
                        } else {
                            let hoverTextInnerHtml = `<div style="display:flex; flex-direction: column; gap: 10px;">`;
                            Object.keys(readyAndNotReadyMediasNames).forEach((key) => {
                                if (readyAndNotReadyMediasNames[key].length && key !== 'ALL_NOT_READY_MEDIA_LIST' &&  key !== 'NOT_READY_DUE_TO_NOT_ROUTABLE_MEDIA_LIST') {
                                    hoverTextInnerHtml += `<div>${readyAndNotReadyMediasNames[key].join(', ')} - ${key}</div>`
                                }
                            });

                            if (signInFailedChannelTitle.length) {
                                hoverTextInnerHtml += `<div>${signInFailedChannelTitle.join(', ')} - ${i18n.getMsg('gadget.imi.fnc.text.signInFailed')}</div>`;
                            }
                            hoverTextInnerHtml += `</div>`;
                            channelData.hoverText = hoverTextInnerHtml;
                            channelData.hoverTextPrefixDisabled = true;
                        }
                    }
            }

            function setFncStateConfig() {
                let curStateConfig;
                if (hasAllMediaLoggedOut) {
                    curStateConfig = CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.LOGOUT);
                    channelData.iconColor = channelService.STATE_STATUS.UNAVAILABLE;
                } else {
                    if(readyAndNotReadyMediasNames[readyLocalString] && readyAndNotReadyMediasNames[readyLocalString].length) {
                        channelData.logoutDisabled = true;
                        curStateConfig = CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.READY);
                    }

                    if (readyAndNotReadyMediasNames[readyLocalString] && readyAndNotReadyMediasNames[readyLocalString].length === allMediaIdsList.length) {
                        curStateConfig = CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.READY);
                        channelData.iconColor = channelService.STATE_STATUS.AVAILABLE;
                    } else if(readyAndNotReadyMediasNames['ALL_NOT_READY_MEDIA_LIST'] && readyAndNotReadyMediasNames['ALL_NOT_READY_MEDIA_LIST'].length === allMediaIdsList.length) {
                        curStateConfig = CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.NOT_READY);
                        channelData.iconColor = channelService.STATE_STATUS.UNAVAILABLE;
                    } else {
                        curStateConfig = curStateConfig || CHANNEL_STATE_MAP.get(AGENT_NON_VOICE_STATES.NOT_READY);
                        channelData.hoverTextPrefixDisabled = true;
                        channelData.iconColor = channelService.STATE_STATUS.BUSY;
                    }
            }
                channelData.curStateMenuId = curStateConfig.menuId;
                channelData.curStateLabel = curStateConfig.label;
                channelData.curStateStatus = curStateConfig.status;
            }
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getChannelDataProps] : all info object : ${JSON.stringify({signInFailedChannelTitle, allMediaIdsList, areAllMediasSignedIn, areMediasPartialSignedIn, hasAllMediaLoggedOut, readyAndNotReadyMediasNames})}`);

            setOtherProps();
            setFncStateConfig();
            setFncChannelHoverText();

            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getChannelDataProps] : final returning channelData : ${JSON.stringify(channelData)}`);

            return channelData;
        }

         // make all clubbed channels as NOT_READY if an agent missen digital interaction
         function handlePostRonaMediaState (mediaId, callbacks) {
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handlePostRonaMediaState] : RONA made for : ${mediaId}`, callbacks);

            const channelType = getChannelType(mediaId);
            handleRonaDesktopNotification(ACTIONS.AGENT_UNAVAILABILITY_PARTIAL_READY, [mediaId]);

            function handleRonaDesktopNotification (actionType, mediaIdsList) {
                if (mediaIdsList && mediaIdsList.length) {
                    let label = '';

                    if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                        label = CHAT_LABEL;
                    } else if(channelType === CHANNEL_TYPES.EMAIL) {
                        label = EMAIL_LABEL;
                    }

                    const channelNames = mediaIdsList.map((mediaId) => getMediaType(mediaId)).join(', ');
                    ImiDesktopNotificationManager.showNotification(actionType, label, channelNames);

                }
            }

            function onSuccess (setStateresults) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handlePostRonaMediaState] : setstae success callback. setStateresults : ${JSON.stringify(setStateresults)}`);
                if (callbacks?.onSuccess && typeof callbacks?.onSuccess === 'function') {
                    console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handlePostRonaMediaState] : calling success callback that was passed `, callbacks?.onSuccess);

                    callbacks?.onSuccess();
                }

                const stateChangePassedMediaIds = setStateresults.filter((res) => res.status === 'fulfilled').map((res) => res.value.mediaId);
                if (stateChangePassedMediaIds.length === setStateresults.length) {
                    handleRonaDesktopNotification(ACTIONS.AGENT_UNAVAILABILITY_ALL_NOT_READY, stateChangePassedMediaIds);
                } else {
                    handleRonaDesktopNotification(ACTIONS.AGENT_UNAVAILABILITY_PARTIAL_READY, stateChangePassedMediaIds);
                }
            }

            function onError (setStateresults) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handlePostRonaMediaState] : setstae error callback. setStateresults : ${JSON.stringify(setStateresults)}`);
            }
            if (callbacks?.onError && typeof callbacks?.onError === 'function') {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handlePostRonaMediaState] : calling error callback that was passed `, callbacks?.onError);

                callbacks?.onError();
            }
            if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                setMediaState(chatChannelId.current, NOT_READY_MENU_ID, onSuccess, onError, true, false, false);
            } else if(channelType === CHANNEL_TYPES.EMAIL) {
                setMediaState(emailChannelId.current, NOT_READY_MENU_ID, onSuccess, onError, true, false, false);
            }
        }

        function handleDesktopNotification (results, isRetry) {
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleDesktopNotification] : results : ${JSON.stringify(results)} and retry : ${isRetry}`);

            if (!results.length) {
                return;
            }

            let chatAndSocialIds = {}, emailIds = {};
            results.forEach((result) => {
                const mediaId = result.status === 'rejected' ? result.reason.mediaId : result.value.mediaId;
                const channelType = getChannelType(mediaId);
                if (channelType && (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL)) {
                    chatAndSocialIds[mediaId] = result.status;
                } else if(channelType && channelType === CHANNEL_TYPES.EMAIL) {
                    emailIds[mediaId] = result.status;
                }
            });

            if (Object.keys(chatAndSocialIds).length) {
                let actionType = '', channelNames = '', label = CHAT_LABEL;

                const successIds = Object.keys(chatAndSocialIds).filter((mediaId) => chatAndSocialIds[mediaId] === 'fulfilled'),
                    failureIds = Object.keys(chatAndSocialIds).filter((mediaId) => chatAndSocialIds[mediaId] === 'rejected');

                if (failureIds.length === (allChannelIds.current.chat.length + allChannelIds.current.social.length)) {
                    actionType = isRetry ? ACTIONS.ALL_RETRY_SIGNIN_FAILED : ACTIONS.ALL_SIGNIN_FAILED;
                } else if (successIds.length === (allChannelIds.current.chat.length + allChannelIds.current.social.length)) {
                    actionType = isRetry ? ACTIONS.ALL_SIGNIN_SUCCESS : '';
                } else {
                    actionType = isRetry ? ACTIONS.RETRY_PARTIAL_SIGNIN : ACTIONS.PARTIAL_SIGNIN;
                    channelNames = failureIds.map((mediaId) => getMediaType(mediaId)).join(', ');
                }
                ImiDesktopNotificationManager.showNotification(actionType, label, channelNames);
            }

            if (Object.keys(emailIds).length) {
                let actionType = '', channelNames = '', label = EMAIL_LABEL;

                const successIds = Object.keys(emailIds).filter((mediaId) => emailIds[mediaId] === 'fulfilled'),
                    failureIds = Object.keys(emailIds).filter((mediaId) => emailIds[mediaId] === 'rejected');

                if (failureIds.length === (allChannelIds.current.email.length)) {
                    actionType = isRetry ? ACTIONS.ALL_RETRY_SIGNIN_FAILED : ACTIONS.ALL_SIGNIN_FAILED;
                } else if (successIds.length === (allChannelIds.current.email.length)) {
                    actionType = isRetry ? ACTIONS.ALL_SIGNIN_SUCCESS : '';
                } else {
                    actionType = isRetry ? ACTIONS.RETRY_PARTIAL_SIGNIN : ACTIONS.PARTIAL_SIGNIN;
                    channelNames = failureIds.map((mediaId) => getMediaType(mediaId)).join(', ');
                }
                ImiDesktopNotificationManager.showNotification(actionType, label, channelNames);
            }
        }

        function cacheMediaStates (mediaListToBeCached) {
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [cacheMediaStates] : mediaListToBeCached : ${mediaListToBeCached}, existing cache : ${JSON.stringify(getAllCachedMediaStatesObj())}`);

            const list = mediaListToBeCached || allMediaObjects.current;
            let toBeCached = getAllCachedMediaStatesObj();

            list?.forEach((media) => {
                const mediaId = media.getId();
                if (media.isLoggedIn() && (media.getState() && media.getState() !== AGENT_NON_VOICE_STATES.NOT_READY)) {
                    toBeCached[mediaId] = AGENT_NON_VOICE_STATES.READY;
                    if (media.getState() === 'ACTIVE') {
                        toBeCached[mediaId] = 'ACTIVE';
                    }
                } else if ((media.isLoggedIn() && (media.getState() && media.getState() === AGENT_NON_VOICE_STATES.NOT_READY)) || !media.isLoggedIn()) {
                    if (toBeCached.hasOwnProperty(mediaId)) {
                        delete toBeCached[mediaId];
                    }
                }
                handleStorage(toBeCached);
            });

            function filterCachedMedias () {
                let cachedObj = getAllCachedMediaStatesObj();
                if (!Object.keys(cachedObj).length) {
                    return;
                }
                const allMediaIds = allMediaObjects.current.map((media) => media.getId());
                Object.keys(cachedObj).forEach((cacheMediaId) => {
                    if (!allMediaIds.includes(cacheMediaId)) {
                        delete cachedObj[cacheMediaId];
                    }
                });
                handleStorage(cachedObj);
            }

            function handleStorage (toBeCachedObj) {
                if (Object.keys(toBeCachedObj).length) {
                    saveToSessionStorage(FINESSE_NON_VOICE_RESTORE_STATE_KEY, JSON.stringify(toBeCachedObj));
                } else {
                    removeFromSessionStorage(FINESSE_NON_VOICE_RESTORE_STATE_KEY);
                }
            }
            filterCachedMedias();
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [cacheMediaStates] : final cached item : ${JSON.stringify(getAllCachedMediaStatesObj())}`);
        }

        function getCachedMediaState (mediaId) {
            let cachedStates = getAllCachedMediaStatesObj();
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [getCachedMediaState] : cached state for ${mediaId} is ${cachedStates[mediaId]}`);

            return cachedStates[mediaId];
        }

        function getAllCachedMediaStatesObj () {
            return getFromSessionStorage(FINESSE_NON_VOICE_RESTORE_STATE_KEY) || {};
        }

        function restoreFncStatesFromCache (ignoreChatRestore = false, ignoreEmailRestore = false, callbacks, isRestorePostFailover = false) {
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreFncStatesFromCache] : Restoring fnc states that are cached...`, {ignoreChatRestore, ignoreEmailRestore});

            let cachedStates = getAllCachedMediaStatesObj();
            let shouldUpdateChatChannel = false, shouldUpdateEmailChannel = false;
            Object.keys(cachedStates).forEach((mediaId) => {
                const channelType = getChannelType(mediaId);
                if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                    shouldUpdateChatChannel = true;
                } else if(channelType === CHANNEL_TYPES.EMAIL) {
                    shouldUpdateEmailChannel = true;
                }
            });
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreFncStatesFromCache] : shouldUpdateChatChannel: ${shouldUpdateChatChannel}, shouldUpdateEmailChannel: ${shouldUpdateEmailChannel}`);

            function onSuccess (setStateresults) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreFncStatesFromCache] : fnc restore states success callback. setStateresults : ${JSON.stringify(setStateresults)}`);
                callbacks && callbacks.onSuccess && callbacks.onSuccess(setStateresults);
                refreshFncStates();
            }

            function onError (setStateresults) {
                console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [restoreFncStatesFromCache] : fnc restore states error callback. setStateresults : ${JSON.stringify(setStateresults)}`);
                callbacks && callbacks.onError && callbacks.onError(setStateresults);
                refreshFncStates();
            }

            let chatUpdated = false, emailUpdated = false;

            if (!ignoreChatRestore && shouldUpdateChatChannel) {
                chatUpdated = true;
                setMediaState(chatChannelId.current, '', onSuccess, onError, false, true, isRestorePostFailover);
            }
            if (!ignoreEmailRestore && shouldUpdateEmailChannel) {
                emailUpdated = true;
                setMediaState(emailChannelId.current, '', onSuccess, onError, false, true, isRestorePostFailover);
            }
            if (!chatUpdated && !emailUpdated) {
                callbacks && callbacks.onSuccess && callbacks.onSuccess();
            }
            refreshFncStates();
        }

        function refreshFncStates () {
            updateFncChannelData(CHANNEL_TYPES.CHAT);
            updateFncChannelData(CHANNEL_TYPES.EMAIL);
        }

        function removeFncChannels () {
            if (chatChannelData.current.channelId) {
                removeChannel(chatChannelData.current.channelId);
            }
            if (emailChannelData.current.channelId) {
                removeChannel(emailChannelData.current.channelId);
            }
        }

        function handleFailback (data) {
            console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [handleFailback] : failback status : ', data.status);
    
            if (data.status === "recovered") {
                console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [handleFailback] : recovery success and hence setting recovered ref to true. ');
                recoveredRef.current = true;
                INITIAL_PROPS.setMediaRestoring(true);
                // cacheMediaStates();
                removeFncChannels();
                // settimeout to wait for all update events to be published and then handle fnc refresh with updated/synchronized states
                setTimeout(() => {
                    
                    let chatId, emailId;

                    mediaIdsOfRonaDuringReconnecting.current.forEach((mediaId) => {
                        const channelType = getChannelType(mediaId);
                        if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                            chatId = mediaId;
                        } else if(channelType === CHANNEL_TYPES.EMAIL) {
                            emailId = mediaId;
                        }

                    });
                    if (chatId) {
                        console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [handleFailback] : making RONA for chatId ');

                        handleNonRoutableEvent(getMediaObj(chatId));
                    }
                    if (emailId) {
                        console.log('Finesse-IMI Gadget : [useDigitalChannel Hook] [handleFailback] : making RONA for emailId ');

                        handleNonRoutableEvent(getMediaObj(emailId));
                    }
                    mediaIdsOfRonaDuringReconnecting.current = [];
                    // Ignoreing this restore because it will be restored in handleNonRoutableEvent if recovered
                    // const ignoreChatRestore = chatId ? true : false;
                    // const ignoreEmailRestore = emailId ? true : false;
                    let ignoreChatRestore = true, ignoreEmailRestore = true;

                    allMediaObjects.current.forEach((media) => {
                        const cachedItems = getAllCachedMediaStatesObj();
                        const cachedMediaIds = Object.keys(cachedItems);
                        if (!cachedMediaIds.length) return;
                        // Means the mrd which was previously ready and been changed to NOT_READY during reconnecting. maybe server restrt
                        if (cachedMediaIds.includes(media.getId()) && (media.getState() === AGENT_NON_VOICE_STATES.NOT_READY || media.getState() === AGENT_NON_VOICE_STATES.LOGOUT)) {
                            const channelType = getChannelType(media.getId());
                            if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                                ignoreChatRestore = false;
                            } else if(channelType === CHANNEL_TYPES.EMAIL) {
                                ignoreEmailRestore = false;
                            }
                        }
                    });

                    restoreFncStatesFromCache(ignoreChatRestore, ignoreEmailRestore, {
                        onSuccess: () => {
                            addChannels();
                            INITIAL_PROPS.setMediaRestoring(false);
                        },
                        onError: () => {
                            addChannels();
                            INITIAL_PROPS.setMediaRestoring(false);
                        }
                    });
                    if (!chatId && !emailId) {
                        addChannels();
                        INITIAL_PROPS.setMediaRestoring(false);
                    }
                    refreshFncStates();
                }, 2000);
            }
        }

        function handleNonRoutableEvent (media) {
            console.log(`Finesse-IMI Gadget : [useDigitalChannel Hook] [handleNonRoutableEvent] : media change event received for : ${media.getId()}, state : ${media.getState()}, routable: ${media.isRoutable()} and recovered : ${recoveredRef.current}`);

            const mediaId = media.getId();
            const onSuccess = function () {
                const channelType = getChannelType(mediaId);
                const callback = () => {
                    //This is a success callback
                    // channels will be removed in handleFailback. so, here add channels again to fnc
                    // hasMediaChangeDetectedDuringDisconnect.current = false; // reset the flag
                    addChannels();
                    INITIAL_PROPS.setMediaRestoring(false);
                }
                if (channelType === CHANNEL_TYPES.CHAT || channelType === CHANNEL_TYPES.SOCIAL) {
                    setMediaState(chatChannelId.current, '', callback, callback, false, true, false);
                } else if(channelType === CHANNEL_TYPES.EMAIL) {
                    setMediaState(emailChannelId.current, '', callback, callback, false, true, false);
                }
            };
            let callbacks;
            // to handle non_routable happening during reconnection phase. Ex: agent internet disconnected and task pushed adn gone rona
            if (recoveredRef.current) {
                // passing success callback to make it ready if it in READY before failback
                callbacks = {onSuccess};
            }
            handlePostRonaMediaState(mediaId, callbacks);
        }

        return {
            initialize,
            addAllChannels,
            handleSigninFailure,
            handleMediaChange,
            getSignInFailedChannelsTitle,
            handlePostRonaMediaState,
            handleDesktopNotification,
            restoreFncStatesFromCache,
            handleFailback,
            cacheMediaStates
        };
}

export default useDigitalChannel;
